import type { AxiosError, AxiosRequestHeaders, AxiosResponse } from "axios";
import type {
  AppAxiosRequestConfig,
  AppAxiosResponse,
  Response,
} from "@/types/http";
import axios from "axios";
import { useLoading } from "@/components/Loading/useLoading";
import { SystemEnum } from "@/constants/system";
import { useAppStore } from "@/store/modules/app";
import { errorHandle } from "./errorHandle";

const { VITE_BASE_URL, DEV } = import.meta.env;
const { open, close } = useLoading();

const instance = axios.create({
  timeout: 60 * 1000,
  baseURL: DEV ? "/gateway" : VITE_BASE_URL,
});

let requestCount = 0;

function requestSuccess(config: AppAxiosRequestConfig) {
  const appStore = useAppStore();
  const {
    headers = {} as AxiosRequestHeaders,
    hiddenLoading,
    toastText,
  } = config;
  headers["system-code"] = SystemEnum.SYSTEM_CODE;
  const [tokenName = "", tokenValue = ""] = appStore.token.split("=");
  if (tokenName) {
    headers[tokenName] = tokenValue;
  }
  // 添加 allianceCode 到请求头
  if (appStore.alliance?.allianceCode) {
    headers["alliance-code"] = appStore.alliance.allianceCode;
  }
  if (hiddenLoading === false) {
    requestCount += 1;
    setTimeout(() => {
      if (requestCount > 0) {
        open(toastText);
      }
    }, 0);
  }
  return {
    ...config,
    headers,
  };
}

function requestError(err: AxiosError) {
  return Promise.reject(err);
}

function responseSuccess(response: AxiosResponse<Response<any>>) {
  if (requestCount > 0) {
    requestCount--;
    close();
  }
  if (response.config.responseType === "blob" && response.status === 200) {
    return Promise.resolve(response);
  }
  if (response.status === 200 && response.data.code === 200) {
    return Promise.resolve(response);
  }
  if (response.status === 200) {
    errorHandle(response.data?.code, response.data?.msg);
  }
  return Promise.reject(response);
}

function responseError(err: AxiosError) {
  console.log("err==>", err);
  if (requestCount > 0) {
    requestCount--;
    close();
  }
  window.$message?.error("网络连接失败，请检查网络后重试!");
  return Promise.reject(err);
}

instance.interceptors.request.use(requestSuccess, requestError);
instance.interceptors.response.use(responseSuccess, responseError);

export function request<T = any>(
  config: AppAxiosRequestConfig,
): Promise<AppAxiosResponse<T>> {
  return instance({
    ...config,
  });
}

export function post<T = any>(config: AppAxiosRequestConfig) {
  return request<T>({
    ...config,
    method: "POST",
  });
}

export function get<T = any>(config: AppAxiosRequestConfig) {
  return request<T>({
    ...config,
    method: "GET",
  });
}

export async function downloadFile<T = any>(config: AppAxiosRequestConfig) {
  const response = await request<T>({
    ...config,
    responseType: "blob",
    timeout: 200 * 1000,
  });
  console.log("response==>", response);
  // 获取response返回流Blob数据
  const blob = response.data as unknown as Blob;
  const data = await blob.text();
  if (!response.headers["content-disposition"])
    return Promise.reject(JSON.parse(data)?.msg);
  const filename = response.headers["content-disposition"]
    .split(";")[1]
    .split("=")[1]
    .trim()
    .replaceAll('"', "");
  const a = document.createElement("a");
  const href = URL.createObjectURL(response.data as unknown as Blob);
  a.href = href;
  a.download = decodeURIComponent(filename);
  a.style.display = "none";
  // 360 等浏览器要求 <a> 必须在 DOM 树中才能触发下载
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(href);
  return Promise.resolve();
}
